import {useLazyQuery, useMutation} from '@apollo/client';
import {useSnackbar} from 'notistack';
import {RequestAtr, RequestAtrVariables} from '../../schema-types/RequestAtr';
import {REQUEST_ATR} from '../AttitudeToRisk/AtrQueries';
import validateUUID from '../../utils/validateUUID';
import {UseRequestAtrParams} from './types';
import {
  checkFactFindPermissionMutation,
  getFactFindTemplates,
} from '../ContactActions/SendFactFindModal';
import {GetFactFindTemplates} from '../../schema-types/GetFactFindTemplates';
import {
  CheckFactFindPermission,
  CheckFactFindPermissionVariables,
} from '../../schema-types/CheckFactFindPermission';

export function useRequestAtr(params: UseRequestAtrParams) {
  const {accountId, contactId, staffId} = params;

  const [mutateFunction, {data, loading, error}] = useMutation<
    RequestAtr,
    RequestAtrVariables
  >(REQUEST_ATR, {
    variables: {
      accountId: validateUUID(accountId)!,
      contactId: validateUUID(contactId)!,
      staffId: validateUUID(staffId)!,
    },
  });

  return {mutateFunction, data, loading, error};
}

export function useCheckFactFindPermissions() {
  const {enqueueSnackbar} = useSnackbar();
  const [onGetTemplates, {loading: onGetTemplatesLoading}] =
    useLazyQuery<GetFactFindTemplates>(getFactFindTemplates);

  const [_onCheckFactFind, {loading: onClientSendFactFindLoading}] = useMutation<
    CheckFactFindPermission,
    CheckFactFindPermissionVariables
  >(checkFactFindPermissionMutation);
  const onCheckFactFind = async (
    variables: Pick<CheckFactFindPermissionVariables, 'contactId' | 'staffId'>
  ) => {
    const templatesData = await onGetTemplates();
    if (templatesData.data?.fincalcTemplates?.length) {
      const checkData = await _onCheckFactFind({
        variables: {
          contactId: validateUUID(variables.contactId)!,
          staffId: validateUUID(variables.staffId),
          templateId: templatesData.data!.fincalcTemplates![0]!.id!,
        },
      });
      if (!checkData.data?.factFindCheckPermission?.ok) {
        enqueueSnackbar({
          variant: 'error',
          message: `Unable to send fincalc (fact find) templates. Error: ${checkData.errors
            ?.map((x) => x.message)
            .join(', ')} ${checkData.data?.factFindCheckPermission?.errors?.join(', ')}`,
        });
      }
    } else {
      enqueueSnackbar({
        variant: 'error',
        message: 'Unable to get fincalc (fact find) templates',
      });
    }
  };
  return {
    onCheckFactFind,
    checkFactFindLoading: onClientSendFactFindLoading || onGetTemplatesLoading || false,
  };
}
